import { editDate } from '@doda/common/src/utils/dateUtil';
import { SavedSearchRequest } from '@doda/api-client/aspida/@types';
import {
  JOB_OFFER_KIND_MAPPING,
  KEYWORD_CATEGORY_MAPPING,
  LOWER_LIMIT_INCOME_MAPPING,
  UPPER_LIMIT_INCOME_MAPPING,
} from '@doda/common/src/constants/hydrogenOldParamMap';
import {
  createSearchConditionName,
  makeSendConditionParam,
} from '@doda/common/src/logics/saveCondition/saveCondition';
import {
  OP_VALUE_MAPPINGS,
  STANDARD_MAPPING,
  CONCAT_NEEDED_MAPPING,
} from 'components/OrverwriteConditionModal/constants';
import { postSearchConditions } from '@doda/common/src/services/conditionRegistAPI';
import { TEMPLATE_CONDITION } from '@doda/common/src/constants/searchConditionModal';

type SavedSearchRequestCondition = Omit<SavedSearchRequest, 'name'>;

/**
 * 「上書きする条件を選択する」ボタン押下時の処理
 * @param {Object} params - 関数の引数オブジェクト
 * @param {SavedSearchRequestCondition} params.conditions - 保存された検索条件
 * @param {string} [params.action] - アクション名（オプション）
 * @param {string} [params.conditionName] - 条件名（オプション）
 */
export const selectSearchCondition = ({
  conditions,
  action,
  conditionName,
}: {
  conditions: SavedSearchRequestCondition;
  action?: string;
  conditionName?: string;
}) => {
  const searchConditionName = conditionName ?? createSearchConditionName();
  const params = action
    ? {
        action,
        wserdt: editDate(),
        wsernm: encodeURI(searchConditionName),
        SP_CC_wsernm: searchConditionName,
      }
    : {
        wserdt: editDate(),
        wsernm: encodeURI(searchConditionName),
        SP_CC_wsernm: searchConditionName,
      };

  // 検索条件保存リクエストパラメータから対応するクエリパラメータに変換
  const newConditions = convertSavedSearchConditionsToQueryParams(conditions);

  // 送信する検索条件データ作成
  const sendCondition = makeSendConditionParam(newConditions);
  const newParams = {
    tp: '1',
    ...TEMPLATE_CONDITION,
    ...params,
    ...sendCondition,
  };
  postSearchConditions('/dcfront/login/login/', newParams, {});
};

/**
 *  検索条件保存リクエストパラメータから対応するクエリパラメータに変換して取得する関数
 * @param {SavedSearchRequestCondition} conditions - 保存された検索条件
 * @returns {Object} - 変換されたクエリパラメータ
 */
export const convertSavedSearchConditionsToQueryParams = (
  conditions: SavedSearchRequestCondition
) => {
  // 標準的なパラメータの取得
  const standardParams = generateStandardQueryParams(conditions);

  // 連結が必要なパラメータの取得
  const concatenationParams = generateConcatenationQueryParams(conditions);

  // OP値の値を取得
  const op = generateOpValues(conditions).join(',') || undefined;

  // 収入範囲のパラメータの取得
  const incomeRangeParams = generateIncomeRangeQueryParams(conditions);

  // 職種種別のパラメータの取得
  const jobOfferKindParams = generateJobOfferKindQueryParams(conditions);

  // キーワードカテゴリのパラメータの取得
  const keywordCategoryParams =
    generateKeywordCategoriesQueryParams(conditions);

  // valueが無効のkeyを削除
  return Object.fromEntries(
    Object.entries({
      ...standardParams,
      ...concatenationParams,
      ...incomeRangeParams,
      ...jobOfferKindParams,
      ...keywordCategoryParams,
      op,
      // eslint-disable-next-line unused-imports/no-unused-vars
    }).filter(([_, value]) => value)
  );
};

/**
 * OP値を変換して取得する関数
 * @param {SavedSearchRequestCondition} conditions - 保存された検索条件
 * @returns {string[]} - 変換されたOP値の配列
 */
export const generateOpValues = (conditions: SavedSearchRequestCondition) => {
  let opValues: string[] = [];
  Object.keys(OP_VALUE_MAPPINGS).forEach((key) => {
    const value = conditions[key];
    if (Array.isArray(value)) {
      value.forEach((item) => {
        const mappedValues = OP_VALUE_MAPPINGS[key][item];
        if (mappedValues) {
          opValues = [...opValues, ...mappedValues];
        }
      });
    } else if (value) {
      opValues = [...opValues, OP_VALUE_MAPPINGS[key]];
    }
  });
  return opValues;
};

/**
 * 収入範囲のパラメータを変換して取得する関数
 * @param {SavedSearchRequestCondition} conditions - 保存された検索条件
 * @returns {Object} - 収入範囲のクエリパラメータ
 */
export const generateIncomeRangeQueryParams = (
  conditions: SavedSearchRequestCondition
) => {
  const lowerLimit = conditions.lowerLimitIncome
    ? LOWER_LIMIT_INCOME_MAPPING[conditions.lowerLimitIncome.toString()]
    : '0';
  const upperLimit = conditions.upperLimitIncome
    ? UPPER_LIMIT_INCOME_MAPPING[conditions.upperLimitIncome.toString()]
    : '0';

  return conditions.lowerLimitIncome || conditions.upperLimitIncome
    ? { ha: `${lowerLimit},${upperLimit}` }
    : {};
};

/**
 * 職種種別のパラメータを変換して取得する関数
 * @param {SavedSearchRequestCondition} conditions - 保存された検索条件
 * @returns {Object} - 職種種別のクエリパラメータ
 */
export const generateJobOfferKindQueryParams = (
  conditions: SavedSearchRequestCondition
) => {
  return conditions.jobOfferKind
    ? { tp: JOB_OFFER_KIND_MAPPING[conditions.jobOfferKind.toString()] }
    : {};
};

/**
 * キーワードカテゴリのパラメータを変換して取得する関数
 * @param {SavedSearchRequestCondition} conditions - 保存された検索条件
 * @returns {Object} - キーワードカテゴリのクエリパラメータ
 */
export const generateKeywordCategoriesQueryParams = (
  conditions: SavedSearchRequestCondition
) => {
  let kwc;
  let niwc;

  // includedKeywordCategoryが設定されていなく、kが設定されている場合、'1'を設定
  if (conditions.includedKeywordCategory) {
    kwc =
      KEYWORD_CATEGORY_MAPPING[conditions.includedKeywordCategory.toString()];
  } else if (conditions.includedKeywordList?.length) {
    kwc = '1';
  }

  // excludedKeywordCategoryが設定されていなく、niが設定されている場合、'1'を設定
  if (conditions.excludedKeywordCategory) {
    niwc =
      KEYWORD_CATEGORY_MAPPING[conditions.excludedKeywordCategory.toString()];
  } else if (conditions.excludedKeywordList?.length) {
    niwc = '1';
  }

  return { kwc, niwc };
};

/**
 * 直接変換できるパラメータを変換して取得する関数
 * @param {SavedSearchRequestCondition} conditions - 保存された検索条件
 * @returns {Object} - 変換された標準クエリパラメータ
 */
export const generateStandardQueryParams = (
  conditions: SavedSearchRequestCondition
) =>
  Object.keys(STANDARD_MAPPING).reduce((queryParams, key) => {
    if (conditions[key]) {
      queryParams[STANDARD_MAPPING[key]] = conditions[key].join(',');
    }
    return queryParams;
  }, {});

/**
 * searchJobTypeLIdList等、まとめる必要があるパラメータを変換して取得する関数
 * @param {SavedSearchRequestCondition} conditions - 保存された検索条件
 * @returns {Object} - 連結が必要なクエリパラメータ
 */
export const generateConcatenationQueryParams = (
  conditions: SavedSearchRequestCondition
) => {
  return Object.keys(CONCAT_NEEDED_MAPPING).reduce((queryParams, key) => {
    if (conditions[key]) {
      const [classfication] = key.match(/.(?=IdList)/) || [];
      const valueToAdd = conditions[key]
        .map((val) => `${val}${classfication}`)
        .join(`,`);
      // 既にパラメータが存在する場合は値を連結し、存在しない場合は新しい値を設定
      const newValue = queryParams[CONCAT_NEEDED_MAPPING[key]]
        ? `${queryParams[CONCAT_NEEDED_MAPPING[key]]}${
            valueToAdd ? `,${valueToAdd}` : ''
          }`
        : valueToAdd;
      // スプレッド演算子で新しいオブジェクトを作成しつつ、特定のキーの値を更新
      return { ...queryParams, [CONCAT_NEEDED_MAPPING[key]]: newValue };
    }

    return queryParams;
  }, {});
};
